import { notifyAuthExpired } from "./authSession";
import { normalizeActiveTurnStatus, normalizeChatConflict } from "../utils/chatPolling";

/**
 * SSE transport for Anna chat (Gemma path).
 *
 * Talks to the always-on anna-chat service on semidiosa via a single
 * `POST /chat` request whose response is a Server-Sent Events stream. The
 * frame shapes (typing / checking / chunk / action_result / done / error)
 * are byte-for-byte the same JSON objects the old WebSocket emitted, so the
 * Chat dispatcher (`handleWsMessage`) consumes them unchanged.
 *
 * Real stop: the Stop button calls `POST /chat/stop {requestId}` (the server
 * halts generation), then aborts the local fetch. A plain disconnect (tab
 * backgrounded, app switched, network blip) is NOT a stop — the server
 * detaches and finishes the turn, persists the reply, and pushes a
 * `notification` frame over the /notifications stream so the UI injects it.
 */

const SSE_URL =
  import.meta.env.VITE_SSE_URL || "https://anna-chat-dev.pretorianhackers.com";
const DOPPELGANGER_URL = import.meta.env.VITE_DOPPELGANGER_URL?.replace(/\/+$/, "") || null;

export function createChatRequestId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `req_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export class ChatSSE {
  constructor(baseUrl = SSE_URL, assistantName = "Anna") {
    this.baseUrl = baseUrl?.replace(/\/+$/, "") || null;
    this.assistantName = assistantName;
    this.onMessage = null;
    this.onStatusChange = null;
    this._token = null;
    this._controller = null;
    this._activeRequestId = null;
    this.stopped = false;
    // Stream-state mirrors the WS client so UI restore logic stays uniform.
    this._streamingContent = "";
    this._streamingActive = false;
    this._thinkingActive = false;
    // The sessionId of the currently in-flight request (set on send).
    this._activeSessionId = null;
  }

  setToken(token) {
    this._token = token;
  }

  get isConfigured() {
    return Boolean(this.baseUrl);
  }

  stop() {
    this.stopped = true;
    this._streamingActive = false;
    this._thinkingActive = false;
    // Tell the server to actually halt generation (disconnects no longer
    // stop the turn), then abort the local stream. Fire-and-forget.
    const requestId = this._activeRequestId;
    const token = this._token || localStorage.getItem("anna_token");
    if (this.baseUrl && requestId && token) {
      fetch(`${this.baseUrl}/chat/stop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ requestId }),
      }).catch(() => {
        /* already finished or unreachable — nothing to stop */
      });
    }
    if (this._controller) {
      try {
        this._controller.abort();
      } catch {
        /* already aborted */
      }
      this._controller = null;
    }
  }

  resetStop() {
    this.stopped = false;
  }

  async activeTurn(sessionId) {
    const token = this._token || localStorage.getItem("anna_token");
    if (!this.baseUrl || !token || !sessionId) return { active: false };
    const url = new URL(`${this.baseUrl}/chat/active`);
    url.searchParams.set("sessionId", sessionId);
    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) notifyAuthExpired("unauthorized");
    if (!res.ok) throw new Error(`Active turn check failed: HTTP ${res.status}`);
    const status = normalizeActiveTurnStatus(await res.json());
    if (status?.active && status.requestId) this._activeRequestId = status.requestId;
    return status;
  }

  /**
   * Send a message and stream the assistant turn as SSE frames.
   * Returns true once the stream completes (or is stopped), false on a
   * connection/setup failure.
   */
  async send(message, sessionId, attachments, { think, requestId = createChatRequestId(), onAccepted } = {}) {
    const token = this._token || localStorage.getItem("anna_token");
    if (!this.baseUrl || !token) return false;

    // Globally unique so /chat/stop can never target another surface's turn
    // (the Chat view and the floating widget each run their own ChatSSE).
    this._activeRequestId = requestId;
    this.stopped = false;
    this._streamingContent = "";
    this._streamingActive = false;
    this._thinkingActive = false;
    this._controller = new AbortController();

    const body = {
      message,
      sessionId: sessionId || `session_${Date.now()}`,
      requestId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    this._activeSessionId = body.sessionId;
    if (think != null) body.think = think;
    if (attachments?.length) body.attachments = attachments;

    try {
      const res = await fetch(`${this.baseUrl}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: this._controller.signal,
      });

      if (!res.ok || !res.body) {
        if (res.status === 401) notifyAuthExpired("unauthorized");
        const fallbackDetail = `HTTP ${res.status}`;
        let detail = fallbackDetail;
        let errorBody = null;
        try {
          errorBody = await res.json();
          detail = typeof errorBody.detail === "string"
            ? errorBody.detail
            : errorBody.error || detail;
        } catch {
          /* non-JSON error body */
        }
        if (res.status === 409) {
          const conflict = normalizeChatConflict(errorBody, fallbackDetail);
          if (conflict.requestId) this._activeRequestId = conflict.requestId;
          this.onMessage?.({
            type: "active_turn",
            message: conflict.message,
            requestId: conflict.requestId || requestId,
            sessionId: this._activeSessionId,
            _sseSessionId: this._activeSessionId,
            active: conflict.active,
            duplicate: conflict.duplicate,
            status: conflict.status,
          });
          return true;
        }
        this.onMessage?.({ type: "error", message: detail, requestId });
        return false;
      }

      onAccepted?.({ requestId, sessionId: this._activeSessionId });
      this.onStatusChange?.("connected");
      await this._readStream(res.body);
      return true;
    } catch (err) {
      // A user-initiated stop aborts the fetch — that's a clean halt, not an
      // error to surface.
      if (err?.name === "AbortError" || this.stopped) return true;
      console.error("SSE request failed:", err);
      if (this._streamingActive || this._thinkingActive || this._streamingContent) {
        // The server already accepted the turn — it keeps processing detached
        // and the reply arrives via the notification stream / a history
        // reload on refocus. Not a fatal error.
        this.onMessage?.({
          type: "stream_detached",
          requestId,
          sessionId: this._activeSessionId,
          _sseSessionId: this._activeSessionId,
        });
        return true;
      }
      this.onMessage?.({
        type: "error",
        message: `Lost connection to ${this.assistantName}. Please try again.`,
        requestId,
      });
      return false;
    } finally {
      this._controller = null;
      if (this._activeRequestId === requestId) this._activeRequestId = null;
      this._streamingActive = false;
      this._thinkingActive = false;
    }
  }

  async _readStream(body) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep;
      // SSE events are separated by a blank line ("\n\n").
      while ((sep = buffer.indexOf("\n\n")) >= 0) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        this._dispatchEvent(rawEvent);
      }
      if (this.stopped) break;
    }
    // Flush any trailing event without a terminating blank line.
    if (buffer.trim()) this._dispatchEvent(buffer);
  }

  _dispatchEvent(rawEvent) {
    const dataPayload = rawEvent
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart())
      .join("\n");
    if (!dataPayload) return;

    let data;
    try {
      data = JSON.parse(dataPayload);
    } catch {
      console.warn("Non-JSON SSE frame:", dataPayload);
      return;
    }

    // Track streaming state for parity with the WS client.
    if (data.type === "typing") {
      this._thinkingActive = true;
      this._streamingActive = true;
      this._streamingContent = "";
    } else if (data.type === "chunk") {
      this._thinkingActive = false;
      this._streamingContent += data.content || "";
    } else if (data.type === "done" || data.type === "error") {
      this._streamingActive = false;
      this._thinkingActive = false;
      this._streamingContent = "";
    }

    // After a stop, swallow any in-flight streaming frames that race the abort.
    if (this.stopped && (data.type === "chunk" || data.type === "checking")) {
      return;
    }

    // Tag the frame with the session that owns this stream so the UI can
    // filter out frames that belong to a different session.
    if (this._activeSessionId) {
      data._sseSessionId = this._activeSessionId;
    }

    this.onMessage?.(data);
  }
}

export const chatSse = new ChatSSE();

// Separate cloud instance so the widget's dispatcher doesn't steal the main
// Chat view's streaming frames.
export const widgetSse = new ChatSSE(DOPPELGANGER_URL, "Anna");

// The cloud route must never share stream state or a base URL with Semidiosa.
export const doppelgangerSse = new ChatSSE(DOPPELGANGER_URL, "Anna");
